import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {
  Alert,
  Box,
  Skeleton,
  Stack,
  RadioGroup,
  Radio,
  Grid,
  Collapse,
  SxProps,
  Theme,
  Button,
} from '@mui/material';
import {VscRefresh} from 'react-icons/vsc';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import FlatInput from '../../flat-input/flat-input';
import {Modal} from '../../modal/modal';
import {LoadingConfigurationsType} from '../../../enums';
import {ConfigurationsIndex, ConfigurationsValuesIndex} from '../../../types';
import {convertArrayToIndex} from '../../../utils/functions';
import {FlatStartsAtDate} from '../../flat-starts-at-date/flat-starts-at-date';
import {FlatStartsAtTime} from '../../flat-starts-at-time/flat-starts-at-time';
import {CareTaskNotDoneReason} from '../../../backend/enums/care-task-not-done-reason';
import {FlatSelectDropdown} from '../../flat-select-dropdown/flat-select-dropdown';
import {DEFAULT_FORM_VALIDATIONS} from '../../../utils/constants';
import {Controller, useForm} from 'react-hook-form';
import {useSnackBar} from '../../../contexts/snack-bar/context';
import {RadioLabel} from '../../shared/forms/radio';
import {ActivitiesGroup} from '../../../backend/interfaces/activities-group';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {CarePlan} from '../../../backend/interfaces/care-plan';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {PutEntityCareTask} from '../../../backend/requests/put-entity-care-task';
import {
  BloodGlucoseData,
  CareTaskData,
  SleepHealthData,
} from '../../../backend/interfaces/care-task-data';
import {CareTaskFeature} from '../../../backend/enums/care-task-feature';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {EntityUserCareTasksRepository} from '../../../repositories/entity-user-care-tasks';
import {EntityUserCarePlansRepository} from '../../../repositories/entity-user-care-plans';
import {HandleCareTaskFeatureDataFields} from '../../shared/handle-care-task-feature-data';
import TaskCard from '../../daily-care-log/task-card/task-card';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {ViewAttachments} from '../../daily-care-log/attachments/view-attachments';
import {
  AddAttachmentsForm,
  SavingAttachment,
} from '../../daily-care-log/attachments/add-attachments-form';
import {CancelEditModal, EditModal, ResetModal} from './modals';
import {requestAttachmentStorageUrl} from '../../../screens/private/daily-care-log/unplanned-tasks/unplannedtask.daily-care-log.screen';
import {StorageType} from '../../../backend/enums';
import {TaskLogs} from '../../history-logs/task-logs/_task-logs';

interface HandleCareTaskModalProps {
  open: boolean;
  onClose: () => void;
  careTasks?: CareTaskComplementary[];
  setCareTasks: Dispatch<SetStateAction<CareTaskComplementary[] | undefined>>;
  modalTask?: CareTaskComplementary;
  userId: string;
  canWrite: boolean;
  displayPendingTime?: boolean;
}

interface FormData {
  time: string;
  date: string;
  markedAsDone?: 'done' | 'not-done';
  taskNotDoneReason?: CareTaskNotDoneReason;
  data?: CareTaskData;
  notes?: string;
  files?: SavingAttachment[];
}

const ON_SET_VALUE_EFFECTS = {shouldDirty: true, shouldValidate: true};

export const HandleCareTaskModal = ({
  open,
  onClose,
  careTasks,
  setCareTasks,
  modalTask,
  userId,
  canWrite,
  displayPendingTime = false,
}: HandleCareTaskModalProps) => {
  const [editMode, setEditMode] = useState(modalTask?.careTask?.markedAsHandled === false);
  const [confirmAction, setConfirmAction] = useState<'EDIT' | 'CANCEL_EDIT' | 'RESET' | undefined>(
    undefined
  );
  const [activitiesGroup, setActivitiesGroup] = useState<ActivitiesGroup | null | undefined>(
    undefined
  );
  const [loadingConfigurations, setLoadingConfigurations] = useState<LoadingConfigurationsType>(
    LoadingConfigurationsType.Loading
  );
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>(undefined);
  const [configurationsValuesIndex, setConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [taskConfigurations, setTaskConfigurations] = useState<
    Array<CarePlanConfiguration> | undefined
  >(undefined);
  const [taskConfigurationsValuesIndex, setTaskConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [types, setTypes] = useState<ActivityType>();
  let {entityId}: {entityId: string} = useParams();
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  const {
    register,
    control,
    setValue,
    getValues,
    watch,
    reset,
    setError,
    clearErrors,
    formState: {isValid, isDirty, errors},
  } = useForm<FormData>({
    ...DEFAULT_FORM_VALIDATIONS,
  });
  const [files] = watch(['files']);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalSx: SxProps<Theme> = {
    marginBottom: 0,
    '.MuiPaper-root': {
      width: '554px',
      borderRadius: '16px',
    },
    '& > .MuiButtonBase-root': {
      lineHeight: '24px',
      height: '36px',
      width: '77px',
      paddingX: '14px',
      borderRadius: '5px',
    },
    '& > .MuiDialogContent-root': {
      paddingBottom: 0,
    },
  };

  const updateTask = async () => {
    if (!entityId || !userId || !modalTask?.careTask.ref || isSubmitting) return;

    const [time, date, markedAsDone, taskNotDoneReason, notes, data] = getValues([
      'time',
      'date',
      'markedAsDone',
      'taskNotDoneReason',
      'notes',
      'data',
    ]);

    const executionDatetime = moment()
      .set({
        date: moment(date).get('date'),
        hours: moment(time).get('hours'),
        minutes: moment(time).get('minutes'),
        seconds: moment(time).get('seconds'),
        milliseconds: moment(time).get('milliseconds'),
      })
      .utc()
      .toISOString();

    const updateData: PutEntityCareTask = {
      markedAsDone: markedAsDone === 'done' ? true : false,
      executionDatetime,
      notes: notes || null,
    };

    setIsSubmitting(true);

    if (files) {
      const result = await requestAttachmentStorageUrl(
        entityId,
        files,
        StorageType.CareTaskAttachment
      );
      if (result === null) {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
        });
        setIsSubmitting(false);
        return;
      } else {
        updateData.attachments = result;
      }
    }

    if (updateData.markedAsDone) {
      if (data && modalTask?.careTask.feature) {
        let cleanData: Record<any, any> = {};

        for (const d in data) {
          // @ts-ignore
          cleanData[d] = data[d];

          // Remove UI select key's
          // @ts-ignore
          if (data[d]?.hasOwnProperty('key')) {
            // @ts-ignore
            cleanData[d] = data[d].key;
          }

          // Convert date to string
          // @ts-ignore
          if (moment.isMoment(data[d])) {
            // @ts-ignore
            cleanData[d] = data[d].toISOString();
          }
        }

        // On the sleep measurement, we want to calculate the distance in minutes between start and end date
        if (modalTask?.careTask.feature === CareTaskFeature.SleepHealthMeasurement) {
          // @ts-ignore
          if (data?.startTime && data?.endTime) {
            var timeDistance = moment.duration(
              // @ts-ignore
              moment(data?.endTime)
                .set('milliseconds', 0)
                // @ts-ignore
                .diff(moment(data?.startTime).set('milliseconds', 0))
            );

            cleanData = {
              ...cleanData,
              value: timeDistance.asMinutes(),
            };
          }
        }

        // @ts-ignore
        updateData.data = cleanData;
      }
    } else {
      updateData.taskNotDoneReason = taskNotDoneReason;
    }

    EntityUserCareTasksRepository.instance
      .HandleCareTask(entityId, userId, modalTask.careTask.ref, updateData)
      .then((response) => {
        const updatedCareTasks = careTasks?.map((careTask) => {
          if (careTask.careTask.ref === response.ref) {
            careTask.careTask = response;
          }
          return careTask;
        });

        setCareTasks(updatedCareTasks);

        handleClose();

        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.care_and_activities.handle_task_modal.snackbar.update.success),
        });
      })
      .catch((error) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.care_and_activities.handle_task_modal.snackbar.update.error),
        });
        setIsSubmitting(false);

        console.debug('🔴 Error handling the care task', error);
      });
  };

  const undoTask = () => {
    if (!entityId || !userId || !modalTask?.careTask.ref) return;

    EntityUserCareTasksRepository.instance
      .ResetCareTask(entityId, userId, modalTask.careTask.ref)
      .then((response) => {
        const updatedCareTasks = careTasks?.map((careTask) => {
          if (careTask.careTask.ref === response.ref) {
            careTask.careTask = response;
          }
          return careTask;
        });

        setCareTasks(updatedCareTasks);

        handleClose();

        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.care_and_activities.handle_task_modal.snackbar.reset.success),
        });
      })
      .catch((error) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.care_and_activities.handle_task_modal.snackbar.reset.error),
        });

        console.debug('🔴 Error handling the care task', error);
      });
  };

  const handleClose = () => {
    // Clean up state
    setIsSubmitting(false);
    setEditMode(false);
    reset();
    hideConfirmationModal();
    setActivitiesGroup(undefined);
    setConfigurationsIndex(undefined);
    setConfigurationsValuesIndex(undefined);
    setLoadingConfigurations(LoadingConfigurationsType.Loading);

    // Run callback
    onClose();
  };

  const getTaskActivityGroupInfo = useCallback(() => {
    if (!modalTask?.careTask) return;

    Promise.all<
      [
        Promise<CarePlan | null>,
        Promise<ActivitiesGroup | null>,
        Promise<Array<CarePlanConfiguration>>,
        Promise<Array<CarePlanConfiguration>>,
        Promise<ActivityType | null>,
      ]
    >([
      // Load Careplan
      EntityUserCarePlansRepository.instance.getCarePlan(
        entityId,
        modalTask.careTask.userId,
        modalTask.careTask.activitiesSectorId
      ),

      // Load Activities Group (to obtain the Configuration layout)
      EntityActivitiesRepository.instance.getGroup(
        entityId,
        modalTask.careTask.activitiesSectorId,
        modalTask.careTask.activitiesGroupId
      ),
      // Load Activity Type Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
        entityId,
        modalTask.careTask.activitiesSectorId,
        modalTask.careTask.activitiesGroupId,
        modalTask.careTask.activityTypeId
      ),
      // Load Activities Group Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getGroupCarePlanConfigurations(
        entityId,
        modalTask.careTask.activitiesSectorId,
        modalTask.careTask.activitiesGroupId
      ),
      EntityActivitiesRepository.instance.getType(
        entityId,
        modalTask.careTask.activitiesSectorId,
        modalTask.careTask.activitiesGroupId,
        modalTask.careTask.activityTypeId
      ),
    ])
      .then(
        ([
          carePlan,
          activitiesGroup,
          typePlanConfiguration,
          groupPlanConfiguration,
          activityTypes,
        ]: [
          CarePlan | null,
          ActivitiesGroup | null,
          Array<CarePlanConfiguration>,
          Array<CarePlanConfiguration>,
          ActivityType | null,
        ]) => {
          if (activitiesGroup) {
            const entry = carePlan?.activitiesGroups?.find(
              (it) => it.activitiesGroupId === activitiesGroup.id
            );

            // Build Configurations Index
            const configurationsIndex = convertArrayToIndex<CarePlanConfiguration>(
              groupPlanConfiguration,
              'id'
            );

            const carePlanActivityType: CarePlanActivitiesGroupType | undefined =
              entry?.activityTypes?.find((it) => it.id === modalTask.careTask.activityTypeEntryId);

            const typeConfigurationValuesIndex =
              carePlanActivityType?.configurations &&
              convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
                carePlanActivityType.configurations,
                'carePlanConfigurationId'
              );

            // Build values index
            const valuesIndex =
              convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
                entry?.configurations ?? [],
                'carePlanConfigurationId'
              );

            setActivitiesGroup(activitiesGroup);

            setConfigurationsIndex(configurationsIndex);

            setTaskConfigurationsValuesIndex(typeConfigurationValuesIndex);

            setConfigurationsValuesIndex(valuesIndex);

            setConfigurationsValuesIndex(valuesIndex);

            setTaskConfigurations(typePlanConfiguration);

            setTypes(activityTypes || undefined);

            setLoadingConfigurations(LoadingConfigurationsType.Loaded);
          } else {
            // Not found - Stop Loading
            setLoadingConfigurations(LoadingConfigurationsType.Error);
          }
        }
      )
      .catch((error: any) => {
        // Log
        console.log(error);

        // Stop Loading
        setLoadingConfigurations(LoadingConfigurationsType.Error);
      });
  }, [modalTask?.careTask, entityId]);

  const setPreffilledFormData = useCallback(() => {
    // if task is done display execution time
    // if task is a pending care task display creation time
    // else display the task start time or current time if date not available
    const dateAndTime =
      modalTask?.careTask.markedAsHandled && modalTask?.careTask.executionDatetime
        ? moment(modalTask.careTask.executionDatetime).toLocaleString()
        : displayPendingTime && modalTask?.pendingCareTask?.createdAt
          ? moment(modalTask?.pendingCareTask?.createdAt).toLocaleString()
          : undefined;

    const defaultValues: FormData = {
      date: dateAndTime || moment().toLocaleString(),
      time: dateAndTime || moment().toLocaleString(),
      taskNotDoneReason: modalTask?.careTask.taskNotDoneReason,
      notes: modalTask?.careTask.notes,
      data: modalTask?.careTask.data,
    };

    if (modalTask?.careTask?.markedAsDone !== undefined) {
      defaultValues.markedAsDone = modalTask?.careTask?.markedAsDone ? 'done' : 'not-done';
    } else if (modalTask?.careTask.pendingCareTaskId) {
      defaultValues.markedAsDone = 'done';
    }

    if (modalTask?.careTask.feature === CareTaskFeature.BloodGlucoseHealthMeasurement) {
      const bloodGlucoseData = defaultValues?.data as BloodGlucoseData | undefined;

      const mealTime = bloodGlucoseData?.mealTime;

      defaultValues.data = {
        ...(defaultValues.data as {}),
        ...(mealTime
          ? {
              mealTime: {
                key: mealTime,
                label: t(
                  translations.care_and_activities.handle_task_modal.feature_input.inputs.mealTime
                    .options[mealTime as 'before-meal' | 'after-meal']
                ),
              },
            }
          : {}),
      } as CareTaskData & {mealTime?: {key: string; label: string}};
    }

    if (modalTask?.careTask.feature === CareTaskFeature.SleepHealthMeasurement) {
      const sleepHealthData = defaultValues?.data as SleepHealthData | undefined;

      defaultValues.data = {
        ...defaultValues.data,
        type: sleepHealthData?.type
          ? {
              key: sleepHealthData?.type,
              label: t(
                translations.care_and_activities.handle_task_modal.feature_input.inputs.type
                  .options[sleepHealthData?.type as 'in-bed' | 'awake' | 'rem' | 'core' | 'deep']
              ),
            }
          : undefined,
        startTime: sleepHealthData?.startTime || moment().toISOString(),
        endTime: sleepHealthData?.endTime || moment().add(1, 'hour').toISOString(),
      } as CareTaskData & {startTime?: string; endTime?: string};
    }

    reset(defaultValues);
  }, [
    modalTask?.careTask.markedAsDone,
    modalTask?.careTask.executionDatetime,
    modalTask?.careTask.taskNotDoneReason,
    modalTask?.careTask.notes,
    modalTask?.careTask.data,
    modalTask?.careTask.pendingCareTaskId,
    modalTask?.careTask.feature,
    modalTask?.pendingCareTask?.createdAt,
    modalTask?.careTask.markedAsHandled,
    displayPendingTime,
    reset,
    t,
  ]);

  const fieldReadEditStyles = useMemo(() => {
    if (editMode && canWrite) {
      return {readOnly: false, disabled: false};
    } else {
      return {readOnly: true, disabled: true};
    }
  }, [editMode, canWrite]);

  // Observe fields to run side-effects
  const [isMarkedAsDone, notDoneReason] = watch(['markedAsDone', 'taskNotDoneReason']);

  const displayNotDoneReason = useMemo(() => isMarkedAsDone === 'not-done', [isMarkedAsDone]);

  const displayFeatureFields = useMemo(
    () => Boolean(isMarkedAsDone === 'done' && modalTask?.careTask?.feature),
    [isMarkedAsDone, modalTask?.careTask?.feature]
  );

  const notesAsMandatory = useMemo(
    () => Boolean(isMarkedAsDone === 'not-done' && notDoneReason === 'other'),
    [isMarkedAsDone, notDoneReason]
  );

  // Methods for user actions
  const displayResetConfirmationModal = () => setConfirmAction('RESET');

  const displayEditConfirmationModal = () => setConfirmAction('EDIT');

  const displayCancelEditConfirmationModal = () => setConfirmAction('CANCEL_EDIT');

  const hideConfirmationModal = () => setConfirmAction(undefined);

  // Observe for the open state and fetch required info
  useEffect(() => {
    if (open) {
      getTaskActivityGroupInfo();
      setPreffilledFormData();
    }
  }, [getTaskActivityGroupInfo, open, modalTask, setValue, setPreffilledFormData]);

  // Observe for task changes and apply edit mode accordingly to the flag of markedAsHandled
  useEffect(() => {
    setEditMode(Boolean(!modalTask?.careTask?.markedAsHandled));
  }, [modalTask, open]);

  // Clean notes error explicitly
  useEffect(() => {
    if (!notesAsMandatory && errors.notes?.message) {
      clearErrors('notes');
    }
  }, [clearErrors, errors.notes?.message, notesAsMandatory]);

  return (
    <>
      <Modal
        preventCloseClick
        id="handle-task-modal"
        title={`${moment(modalTask?.careTask.startsAt).format('HH:mm')} -
                ${modalTask?.careTask?.nameConfigurationValue || modalTask?.activityTypeName || ''}`}
        open={open && !confirmAction}
        onClose={onClose}
        // @ts-ignore
        actions={[
          {
            label: t(translations.care_and_activities.handle_task_modal.cancel),
            type: 'destructive',
            onClick: isDirty ? displayCancelEditConfirmationModal : handleClose,
            disabled: isSubmitting,
          },
          canWrite
            ? editMode
              ? {
                  label: modalTask?.careTask.pendingCareTaskId
                    ? t(translations.office.pending_tasks.modal_validate)
                    : t(translations.care_and_activities.handle_task_modal.treated),
                  type: 'nondestructive',
                  onClick:
                    modalTask?.careTask?.markedAsHandled === true
                      ? displayEditConfirmationModal
                      : updateTask,
                  disabled: !isValid || isSubmitting,
                  loading: isSubmitting,
                }
              : {
                  label: t(translations.care_and_activities.handle_task_modal.edit_entry),
                  type: 'nondestructive',
                  variant: 'text',
                  onClick: () => setEditMode(true),
                  sx: {textDecoration: 'underline'},
                }
            : false,
        ].filter(Boolean)}
        sx={modalSx}
      >
        {loadingConfigurations === LoadingConfigurationsType.Loading && (
          <Stack gap={2}>
            <Skeleton animation="wave" variant="rounded" height="60px" />
            <Skeleton animation="wave" variant="rounded" height="120px" />
          </Stack>
        )}

        {loadingConfigurations === LoadingConfigurationsType.Error && (
          <Stack gap={2}>
            <Alert
              title={t(translations.care_and_activities.handle_task_modal.task_info_load_error)}
            />
          </Stack>
        )}

        {loadingConfigurations === LoadingConfigurationsType.Loaded && (
          <Stack flexDirection="column" gap={2}>
            <TaskCard
              displayType="task"
              defaultExpand
              activitiesGroup={activitiesGroup ? activitiesGroup : undefined}
              configurationsIndex={configurationsIndex}
              configurationsValuesIndex={configurationsValuesIndex}
              taskConfigurationsValuesIndex={taskConfigurationsValuesIndex}
              taskConfigurations={taskConfigurations}
              types={types}
              taskDetails={modalTask}
              taskInformation
            />

            <Box component="form" display="flex" flexDirection="column" gap={3}>
              {/**
               * Execution time
               */}
              <Grid container columnSpacing={2} rowSpacing={4}>
                <Grid item xs={6}>
                  <Controller
                    name="time"
                    control={control}
                    render={({field}) => (
                      // @ts-ignore
                      <FlatStartsAtTime
                        {...field}
                        label={t(translations.care_and_activities.handle_task_modal.time_input)}
                        {...fieldReadEditStyles}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="date"
                    control={control}
                    render={({field}) => (
                      // @ts-ignore
                      <FlatStartsAtDate
                        {...field}
                        label={t(translations.care_and_activities.handle_task_modal.date_input)}
                        {...fieldReadEditStyles}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              {/**
               * Mark as done
               */}
              <Controller
                name="markedAsDone"
                control={control}
                rules={{
                  required: true,
                }}
                render={({field}) => (
                  <RadioGroup row {...field}>
                    <Grid container columnSpacing={2} rowSpacing={4}>
                      <Grid item xs={6}>
                        <RadioLabel
                          value="done"
                          control={<Radio />}
                          label={t(
                            translations.care_and_activities.handle_task_modal.task_status.done
                          )}
                          checked={field.value === 'done'}
                          {...fieldReadEditStyles}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <RadioLabel
                          value="not-done"
                          control={<Radio />}
                          label={t(
                            translations.care_and_activities.handle_task_modal.task_status.not_done
                          )}
                          checked={field.value === 'not-done'}
                          {...fieldReadEditStyles}
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                )}
              />
              {/**
               * Not Done - reason
               */}
              <Collapse in={displayNotDoneReason} unmountOnExit>
                <Grid container columnSpacing={2} rowSpacing={4} item xs={12}>
                  <Grid item xs={6}>
                    <Controller
                      name="taskNotDoneReason"
                      control={control}
                      rules={{
                        required: true,
                      }}
                      render={({field: {value, onBlur}}) => (
                        <FlatSelectDropdown
                          label={t(translations.daily_care_log.task_details.task_not_done_reason)}
                          placeholder={t(
                            translations.daily_care_log.task_details.task_not_done_reason
                          )}
                          value={
                            value
                              ? {
                                  key: value,
                                  label: t(translations.care_taks_not_done_reasons[value]),
                                }
                              : undefined
                          }
                          onChange={(option) => {
                            setValue(
                              'taskNotDoneReason',
                              option.key as CareTaskNotDoneReason,
                              ON_SET_VALUE_EFFECTS
                            );
                          }}
                          onBlur={onBlur}
                          options={Object.values(CareTaskNotDoneReason).map((option) => {
                            return {
                              key: option,
                              label: t(translations.care_taks_not_done_reasons[option]),
                            };
                          })}
                          {...fieldReadEditStyles}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Collapse>
              {/**
               * Data feature fields
               */}
              {modalTask?.careTask.feature && (
                <Collapse in={displayFeatureFields} unmountOnExit>
                  <HandleCareTaskFeatureDataFields
                    layout="office"
                    feature={modalTask.careTask.feature}
                    control={control}
                    readOnly={!editMode}
                  />
                </Collapse>
              )}
              {/**
               * Notes
               */}
              <FlatInput
                {...register('notes', {
                  required: {
                    value: notesAsMandatory,
                    message: t(
                      translations.care_and_activities.handle_task_modal
                        .observation_required_message
                    ),
                  },
                })}
                placeholder={t(
                  translations.care_and_activities.handle_task_modal.write_observation
                )}
                label={t(translations.care_and_activities.handle_task_modal.observations)}
                rows={4}
                multiline
                required={notesAsMandatory}
                errors={errors.notes?.message}
                {...fieldReadEditStyles}
              />
              {/**
               * Hability to reset a task
               */}
              {canWrite && editMode && modalTask?.careTask?.markedAsHandled && (
                <Stack
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    color="error"
                    variant="text"
                    startIcon={<VscRefresh />}
                    sx={{maxWidth: '252px', width: '100%'}}
                    onClick={displayResetConfirmationModal}
                  >
                    {t(translations.care_and_activities.handle_task_modal.undo_task)}
                  </Button>
                </Stack>
              )}
              {/**
               * Task attachments
               */}
              {modalTask?.careTask.attachments &&
                modalTask.careTask.attachments.length > 0 &&
                !editMode && <ViewAttachments attachments={modalTask.careTask.attachments} />}
              {editMode && (
                <Controller
                  name="files"
                  control={control}
                  render={() => {
                    return (
                      <AddAttachmentsForm
                        type="view"
                        attachmentsUrls={modalTask?.careTask.attachments}
                        onAttachmentsChange={(value) => setValue('files', value)}
                        onError={(errors) => {
                          if (errors.length > 0) {
                            setError('files', {message: 'Error'});
                          } else {
                            clearErrors('files');
                          }
                        }}
                      />
                    );
                  }}
                />
              )}
              {/**
               * Task history
               */}
              {((modalTask?.careTask.changesHistory &&
                modalTask?.careTask.changesHistory.length > 0) ||
                (modalTask?.careTask.history && modalTask?.careTask.history.length > 0)) && (
                <TaskLogs
                  history={modalTask?.careTask.changesHistory}
                  legacyHistory={modalTask?.careTask.history}
                  feature={modalTask?.careTask.feature}
                />
              )}
            </Box>
          </Stack>
        )}
      </Modal>

      {/**
       * Confirm reset task
       */}
      {confirmAction === 'RESET' && (
        <ResetModal onCancel={hideConfirmationModal} onConfirm={undoTask} />
      )}

      {/**
       * Confirm task edit
       */}
      {confirmAction === 'EDIT' && (
        <EditModal
          isSubmitting={isSubmitting}
          onCancel={hideConfirmationModal}
          onConfirm={updateTask}
        />
      )}

      {/**
       * Confirm task edit cancel
       */}
      {confirmAction === 'CANCEL_EDIT' && (
        <CancelEditModal
          onClose={onClose}
          onCancel={hideConfirmationModal}
          onConfirm={handleClose}
        />
      )}
    </>
  );
};
